import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout from '../common/GuideLayout';
import GuideCta from '../common/GuideCta';
import TemplateCard from '../common/TemplateCard';
import RelatedGuides from '../common/RelatedGuides';
import { relatedTo } from '../common/rsvpCluster';
import { SITE_SIGNUP_URL } from '../signup/startIntent';
import shotInvitation from '../../../assets/rsvp-1-invitation.webp';
import shotEvenements from '../../../assets/rsvp-2-evenements.webp';
import shotMenu from '../../../assets/rsvp-3-menu.webp';
import shotConfirmation from '../../../assets/rsvp-4-confirmation.webp';
import { FAQ, FEATURES, READING_MINUTES, REMINDER_TIPS, SHOTS, STEPS, TEMPLATE_GROUPS, TOC, UPDATED } from './content';

const IMAGES: Record<string, string> = {
  invitation: shotInvitation,
  evenements: shotEvenements,
  menu: shotMenu,
  confirmation: shotConfirmation,
};

const SiteCta: React.FC<{ title: string; text: string; label?: string }> = ({
  title,
  text,
  label = 'Créer mon site d’invitation gratuit',
}) => <GuideCta to={SITE_SIGNUP_URL} title={title} text={text} label={label} />;

// Article « RSVP mariage », page centrale du sujet : ce que c'est, le RSVP en
// ligne (parcours invité en captures), liens vers QR code / formulaire / Google
// Form, date limite, modèles de textes, relances, FAQ.
const RsvpGuidePage: React.FC = () => (
  <GuideLayout
    eyebrow="Guide · Invitations"
    title="RSVP mariage : la réponse de vos invités, en ligne et sans effort"
    updated={UPDATED}
    published="2026-10-15"
    readingMinutes={READING_MINUTES}
    lead="Fini les réponses éparpillées entre SMS, emails et coups de fil. Avec un RSVP en ligne, vos invités répondent en deux minutes depuis leur téléphone, et vous savez à tout moment qui vient, à quoi, et ce qu’il mange. Mode d’emploi, date limite, relances et modèles de textes à copier."
    topCta={
      <SiteCta
        title="Votre RSVP en ligne, gratuit"
        text="Une page d’invitation à vos prénoms, avec lien et QR code : vos invités répondent événement par événement, et tout arrive au même endroit."
      />
    }
    toc={TOC}
    faq={FAQ}
  >
    <section id="definition">
      <h2>Que veut dire RSVP sur un faire-part ?</h2>
      <p>
        RSVP est l’abréviation de <b>« Répondez s’il vous plaît »</b>. Placée sur un faire-part, elle demande à vos
        invités de confirmer leur présence, en général avant une date précise. C’est grâce à ces réponses que vous
        donnerez le nombre de couverts au traiteur et que vous ferez votre plan de table.
      </p>
      <p>
        Longtemps, on répondait par courrier ou par téléphone. Aujourd’hui, la plupart des couples proposent une réponse
        en ligne : plus simple pour les invités, et beaucoup plus simple à suivre pour vous.
      </p>
    </section>

    <section id="en-ligne">
      <h2>Le RSVP en ligne, comment ça marche</h2>
      <ol className="mg-steps">
        {STEPS.map((s) => (
          <li key={s.title}>
            <h3>{s.title}</h3>
            <p>{s.text}</p>
          </li>
        ))}
      </ol>
      <div className="mg-phones">
        {SHOTS.map((s) => (
          <figure key={s.key}>
            <img src={IMAGES[s.key]} width={420} height={909} loading="lazy" decoding="async" alt={s.caption} />
            <figcaption>{s.caption}</figcaption>
          </figure>
        ))}
      </div>
      <h3 className="mg-subtitle">Ce que vos invités peuvent vous dire</h3>
      <div className="mg-grid">
        {FEATURES.map((f) => (
          <div key={f.title} className="mg-card">
            <h3>{f.title}</h3>
            <p>{f.text}</p>
          </div>
        ))}
      </div>
      <SiteCta
        title="Prêt en 2 minutes"
        text="Vos prénoms, votre date, vos événements : votre page d’invitation est en ligne, et les réponses arrivent dans votre liste d’invités."
      />
    </section>

    <RelatedGuides id="outils" title="QR code, formulaire ou Google Form ?" items={relatedTo('rsvp')} />

    <section id="date-limite">
      <h2>Quelle date limite de réponse ?</h2>
      <p>
        Indiquez une date limite <b>4 à 6 semaines avant le mariage</b>. Votre traiteur vous demandera le nombre de
        couverts environ deux à trois semaines avant : cette marge vous laisse le temps de relancer les retardataires et
        de finaliser votre <Link to="/plan-de-table-mariage">plan de table</Link>.
      </p>
      <p>
        <Link to="/quand-envoyer-faire-part-mariage">Envoyez les faire-part 4 à 6 mois avant le mariage</Link>, et les
        save the date plus tôt encore : voir notre <Link to="/retroplanning-mariage">rétroplanning de mariage</Link>.
      </p>
    </section>

    <section id="textes">
      <h2>Modèles de textes à copier</h2>
      <p>
        Remplacez simplement ce qui est entre crochets. Pour la version papier, voir nos{' '}
        <Link to="/formulaire-reponse-mariage">modèles de carton réponse</Link>.
      </p>
      {TEMPLATE_GROUPS.map((g) => (
        <div key={g.id} className="mg-template-group">
          <h3>{g.title}</h3>
          <p className="mg-template-intro">{g.intro}</p>
          <div className="mg-templates">
            {g.items.map((item) => (
              <TemplateCard key={item.title} template={item} />
            ))}
          </div>
        </div>
      ))}
    </section>

    <section id="relancer">
      <h2>Relancer les invités sans réponse</h2>
      <p>Il y a toujours des retardataires. Quelques conseils pour les relancer sans lourdeur :</p>
      <ul className="mg-bullets">
        {REMINDER_TIPS.map((tip) => (
          <li key={tip}>{tip}</li>
        ))}
      </ul>
      <SiteCta
        title="Sachez exactement qui relancer"
        text="Dans votre liste d’invités, filtrez ceux qui n’ont pas encore répondu, et notez vous-mêmes les réponses reçues par téléphone."
        label="Créer mon espace gratuit"
      />
    </section>
  </GuideLayout>
);

export default RsvpGuidePage;
